// The Accelerator's home (Accelerator.dc.html at laptop width, Returning member; its First visit's
// welcome checklist, Payment overdue's banner and the read-only line while staff view as a member
// are drawn above it by the product route, and Access suspended is the paused page). No board draws
// it on a phone, so there it follows PhoneWeek.dc.html's and PhoneLibrary.dc.html's pieces: 16px
// gutters, the cards 1 column. The sections follow the published home layout, with MASTERMIND_HOME
// where none is published (src/product/layout.js):
//
//   news        Adam's announcement for the Accelerator, while he has 1 up
//   goal        Your goal: the goal (Locked <date>, or the member's draft), the month of 12 and its bar; under it
//               View your accountability log, the 1 way into the log from here, and on a laptop, when they're
//               behind, the refocus card (Adam, 18 Oct, 16:12)
//   next        the next session: date, its time from start to end, Zoom, Join, Add to calendar and All sessions,
//               attendance from their accountability log and where they stand (the pill opens the log, where the
//               mastermind's settings show it: guaranteeOf), and the latest session they missed still to watch
//   actions     this month's actions, ticked as they go, each with a note for Adam, and when they're due (fixed: it
//               moves, never hides)
//   group       Your group: the group's own WhatsApp link
//   wins        Your wins, the latest 3 with See all your wins, and Add a win
//   recordings  the latest recording of their group, with Watch and All recordings
//   res         Resources: a shortcut to each tab, with its count
//
// The Accelerator critique (20 Oct): their accountability log is read with the rest of the page (data.js
// loadMastermind), so the attendance line, the pill and the refocus card are there on the first draw and nothing jumps
// in; while the refocus card shows it says the verdict, so the next session has no pill and no line (members 1, 3 and
// 11); the home is shorter (member 5). Below 1100px and on a phone the order is the goal with its big log button, the
// next session, the refocus card (its own part there, not a layout section), this month's actions, then the rest in the
// layout's order, every day, a session day too (Adam's answer to question 4: bands.js stackedKeys). The laptop stays as
// signed off. With the goal switched off in Layout there's no refocus card, so the next session keeps its pill and line.
//
// src/products/accelerator/ has the pieces; its model.js works out what they say.
import { useMember } from '../../app/member.jsx'
import { useNow } from '../../lib/clock.js'
import { useMedia } from '../../lib/media.js'
import { useViewingAs } from '../../lib/viewAs.jsx'
import { useProductData } from '../../product/ProductData.jsx'
import { STACK_QUERY, useLayout } from '../../product/Layout.jsx'
import { MASTERMIND_HOME } from '../../product/layout.js'
import { ProductAnnouncement } from '../../product/ProductAnnouncement.jsx'
import { myIntake, productAnnouncement } from '../../product/model.js'
import { resourceTabs } from '../../product/release.js'
import { useOwnedPaths } from '../../product/Resources.jsx'
import { HomeSections } from '../../products/accelerator/Bands.jsx'
import { GoalCard, GroupCard, LogButton, NextSessionCard, RefocusCard } from '../../products/accelerator/Cards.jsx'
import { ActionsCard } from '../../products/accelerator/Actions.jsx'
import { WinsCard } from '../../products/accelerator/Wins.jsx'
import { LatestRecording } from '../../products/accelerator/LatestRecording.jsx'
import { ResourceShortcuts } from '../../products/accelerator/Shelf.jsx'
import { LoadFailed, Loading, useAccelerator } from '../../products/accelerator/Page.jsx'
import {
  cyclesView, dueLine, goalView, guaranteeOf, missedToWatch, nextSessionView, refocusView, standingFor, standingView, winsView,
} from '../../products/accelerator/model.js'

// Accelerator.dc.html: the goal beside the next session, this month's actions beside the group and
// the wins, 1.45 to 1 with 24px between
const COLUMNS = 'minmax(0, 1.45fr) minmax(0, 1fr)'
// the home shows the latest 3 wins (member 5)
const WINS_SHOWN = 3

export default function MastermindHome({ product }) {
  const { data } = useProductData()
  const { me, lib } = useMember()
  const nowMs = useNow()
  const viewingAs = useViewingAs()
  const owned = useOwnedPaths()
  const stacked = useMedia(STACK_QUERY)
  // the log read again quietly when the rest was read before the home opened, so a register mark or a month Adam
  // closed since shows here as on the log page
  const mm = useAccelerator(product, { freshLog: true })
  const sections = useLayout(product, 'home', MASTERMIND_HOME)

  if (!sections || mm.status === 'loading') return <Loading className="acc-home" />
  if (mm.status === 'error' || !mm.data) return <LoadFailed onRetry={mm.reload} />

  const p = data.product || product
  const personId = me.person.id
  const standing = standingFor(mm.data.standing, p.id)
  const group = standing?.cohort_name || myIntake(data)?.name || null
  const month = cyclesView(mm.data.cycles, mm.data.actions, data.sessions).open
  const announcement = productAnnouncement(lib.data, p.id, nowMs)
  const hasResources = resourceTabs(data, nowMs, owned).length > 0
  const write = { productId: p.id, personId, viewingAs, patch: mm.patch }
  const logHref = `${p.path}/accountability-log`
  // their accountability log, read with the page (null if it didn't come): no attendance line, pill or card without it
  const log = mm.data.log || null
  // the actions carried into this month and not ticked, named on the refocus card from the page's own rows, so a tick
  // here shows at once (member 6)
  const carried = (month?.rows || []).filter(r => r.carriedFrom && !r.done).map(r => r.body)
  // behind, the refocus card, where the mastermind's settings show where they stand (guaranteeOf): on a laptop under
  // the button, so the goal stops matching the next session's height and that card doesn't stretch to it; on a phone
  // its own part, after the next session
  const refocus = guaranteeOf(p) ? refocusView(log, mm.data.goal, { carried, logHref }) : null
  const next = nextSessionView({ product: p, sessions: data.sessions, groups: data.cohorts, nowMs })
  const goalShown = sections.some(s => s.key === 'goal' && s.shown)
  // the card is drawn only with the goal (beside its button, or after the next session on stacked widths): while it
  // shows it says the verdict, else the next session's pill and line do
  const refocusShown = Boolean(refocus) && goalShown

  const parts = {
    news: { place: 'full', when: Boolean(announcement), render: () => <ProductAnnouncement announcement={announcement} /> },
    goal: {
      place: 'main', columns: COLUMNS, stretch: !refocus,
      render: () => (
        <>
          <GoalCard view={goalView(mm.data.goal, standing)} goal={mm.data.goal} {...write} />
          <div className="acc-focus">
            <LogButton href={logHref} />
            {!stacked && <RefocusCard view={refocus} />}
          </div>
        </>
      ),
    },
    refocus: { place: 'main', when: stacked && goalShown && Boolean(refocus), render: () => <RefocusCard view={refocus} /> },
    next: {
      place: 'side', columns: COLUMNS, stretch: !refocusShown,
      render: () => (
        <NextSessionCard view={next} missed={missedToWatch(log, data, p)}
          standing={standingView(standing, log, logHref, guaranteeOf(p), { refocus: refocusShown })} />
      ),
    },
    actions: {
      place: 'main', columns: COLUMNS,
      render: () => <ActionsCard month={month} viewingAs={viewingAs} patch={mm.patch} notes due={dueLine(data.sessions, nowMs)} />,
    },
    group: { place: 'side', columns: COLUMNS, render: () => <GroupCard group={group} url={data.whatsappUrl} /> },
    wins: { place: 'side', columns: COLUMNS, render: () => <WinsCard wins={winsView(mm.data.wins, nowMs)} limit={WINS_SHOWN} {...write} /> },
    recordings: { place: 'full', render: () => <LatestRecording product={p} /> },
    res: { place: 'full', when: hasResources, render: () => <ResourceShortcuts product={p} /> },
  }
  // on stacked widths, every day: the goal and its button, the next session, the refocus card, this month's actions
  const stack = ['goal', 'next', 'refocus', 'actions']

  return (
    <div className="acc-home">
      <h1 className="sr-only">{p.name}</h1>
      <HomeSections sections={sections} parts={parts} stack={stack} />
    </div>
  )
}
